import React from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { MessageCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ChatErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
}

export const ChatErrorBoundary: React.FC<ChatErrorBoundaryProps> = ({ 
  children, 
  onReset 
}) => {
  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  return (
    <ErrorBoundary
      level="section"
      onError={(error, errorInfo) => {
        // Immediately log to console for debugging
        console.error('🚨 [ChatErrorBoundary] Error caught:', error);
        console.error('🚨 [ChatErrorBoundary] Error message:', error.message);
        console.error('🚨 [ChatErrorBoundary] Error stack:', error.stack);
        console.error('🚨 [ChatErrorBoundary] Component stack:', errorInfo.componentStack);
      }}
      fallback={
        <div className="flex items-center justify-center h-full p-6">
          <Alert variant="destructive" className="max-w-md">
            <MessageCircle className="h-5 w-5" />
            <AlertTitle>Chat Error</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4">
                The chat widget encountered an error and couldn't load properly.
              </p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleReset}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Restart Chat
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
};
